import { Card } from "@design";
import { useQuery } from "@tanstack/react-query";
import { Clock, Database, RefreshCw, ShieldCheck, User } from "lucide-react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import type { MicroAppInfo, MicroAppResponse } from "@/api";
import { getMicroAppInfoOptions } from "@/api";
import { Text } from "@/components/Text";
import { formatDateTime, formatRelativeTime } from "@/i18n/format";

/** About tab: the full info panel (identity, authorship, provenance) in a Card. */
export function MicroAppAboutTab({ app }: Readonly<{ app: MicroAppResponse }>) {
  const info = useQuery(getMicroAppInfoOptions({ path: { app_uuid: app.uuid } }));
  return (
    <Card>
      <MicroAppAboutBody info={info.data?.data} app={app} />
    </Card>
  );
}

/**
 * The About/Info content, bare — reused by the viewer's About modal (no Card
 * shell). Renders the full metadata panel the old UI had: the about text, then
 * labeled icon rows for authorship (published by / when) and provenance (data
 * sources with field counts, refresh cadence, last-updated, and the access
 * summary in plain language).
 */
export function MicroAppAboutBody({ info, app }: Readonly<{ info: MicroAppInfo | undefined; app: MicroAppResponse }>) {
  const { t } = useTranslation("microApps");
  const data = info;

  if (!data) {
    return (
      <Text variant="body-sm" tone="tertiary">
        {t("about.empty")}
      </Text>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {data.about && (
        <Text variant="body-sm" tone="secondary">
          {data.about}
        </Text>
      )}

      {/* Authorship */}
      <div className="flex flex-col border-t border-border-subtle pt-2">
        <InfoRow icon={<User size={16} />} label={t("about.publishedBy")}>
          {data.createdByName ?? t("about.unknown")}
          {data.generatedFromChat && (
            <Text as="span" variant="body-sm" tone="tertiary">
              {" "}
              · {t("about.fromChat")}
            </Text>
          )}
        </InfoRow>
        <InfoRow icon={<Clock size={16} />} label={t("about.published")}>
          {formatDateTime(data.publishedAt ?? app.createdAt)}
        </InfoRow>
      </div>

      {/* Provenance */}
      <div className="flex flex-col border-t border-border-subtle pt-2">
        {data.isLive ? (
          <>
            {(data.dataSources?.length ?? 0) > 0 && (
              <InfoRow icon={<Database size={16} />} label={t("about.dataSources")}>
                <ul className="flex flex-col gap-0.5">
                  {data.dataSources?.map((s) => (
                    <li key={s.provider}>
                      <Text as="span" variant="body-sm">
                        {s.label}
                      </Text>{" "}
                      <Text as="span" variant="body-sm" tone="tertiary">
                        · {t("about.fieldCount", { count: s.bindingCount })}
                      </Text>
                    </li>
                  ))}
                </ul>
              </InfoRow>
            )}
            {data.refreshLabel && (
              <InfoRow icon={<RefreshCw size={16} />} label={t("about.refreshes")}>
                {t("about.refreshesOnOpen", { label: data.refreshLabel })}
              </InfoRow>
            )}
            {data.lastUpdated && (
              <InfoRow icon={<Clock size={16} />} label={t("about.dataLastUpdated")}>
                {formatRelativeTime(data.lastUpdated)}
              </InfoRow>
            )}
            {data.authSummary && (
              <InfoRow icon={<ShieldCheck size={16} />} label={t("about.access")}>
                {data.authSummary}
              </InfoRow>
            )}
          </>
        ) : (
          <Text variant="body-sm" tone="secondary">
            {t("about.static")}
          </Text>
        )}
      </div>
    </div>
  );
}

/** One labeled icon row in the About panel (the old UI's InfoRow). */
function InfoRow({ icon, label, children }: Readonly<{ icon: ReactNode; label: string; children: ReactNode }>) {
  return (
    <div className="flex items-start gap-3 py-1.5">
      <span className="mt-0.5 shrink-0 text-content-tertiary">{icon}</span>
      <div className="min-w-0">
        <Text variant="caption" tone="tertiary" className="uppercase tracking-wide">
          {label}
        </Text>
        <div className="text-content-primary [font:var(--type-body-sm)]">{children}</div>
      </div>
    </div>
  );
}
